import React, { useState, useEffect, useCallback } from 'react';
import {
	Grid,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Alert,
} from '@mui/material';
import { IOnboardingSettings } from 'core/interfaces/onboardingInterface';
import { createOnboardingSettings } from 'core/API/onboarding';
import { getPartners } from 'core/API/partners';
import { Partner } from 'types';
import { Button as UiButton } from '../../../../components/ui/button';
import styles from '../SchedulerSetting.module.scss';
import {
	filterStoragePartners,
	findPartnerByIri,
	getPartnerIri,
	isStorageProviderUnset,
	resolveStoragePartner,
} from './onboardingPartnerUtils';

interface EditGeneralSettingsProps {
	settings: IOnboardingSettings | null;
	allPartners?: Partner[];
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

const EditGeneralSettings: React.FC<EditGeneralSettingsProps> = ({
	settings,
	allPartners,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const [settingsBuffer, setSettingsBuffer] = useState(() => {
		const getWorkplace = () => {
			if (settings?.workplace) {
				return settings.workplace;
			}
			const workplaceId = localStorage.getItem('workplace-id');
			return workplaceId ? `/api/workplaces/${workplaceId}` : '';
		};

		return {
			workplace: getWorkplace(),
			ein: settings?.ein || '',
			partnerId: '',
		};
	});

	const [partners, setPartners] = useState<Partner[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingPartners, setIsLoadingPartners] = useState(!allPartners?.length);
	const [error, setError] = useState<string | null>(null);
	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

	const applyPartnerResolution = useCallback(
		(storagePartners: Partner[]) => {
			const resolved = resolveStoragePartner(settings, storagePartners);
			if (resolved) {
				setSettingsBuffer((prev) => ({
					...prev,
					partnerId: getPartnerIri(resolved),
				}));
				return;
			}

			if (isStorageProviderUnset(settings)) {
				setSettingsBuffer((prev) => ({
					...prev,
					partnerId: '',
				}));
				return;
			}

			if (!settings && storagePartners.length === 1) {
				setSettingsBuffer((prev) => {
					if (prev.partnerId) return prev;
					return {
						...prev,
						partnerId: getPartnerIri(storagePartners[0]),
					};
				});
			}
		},
		[settings]
	);

	useEffect(() => {
		if (allPartners?.length) {
			const storagePartners = filterStoragePartners(allPartners);
			setPartners(storagePartners);
			applyPartnerResolution(storagePartners);
			setIsLoadingPartners(false);
			return;
		}

		const loadPartners = async () => {
			try {
				setIsLoadingPartners(true);
				const partnersData = await getPartners({ active: true });
				const storagePartners = filterStoragePartners(partnersData);
				setPartners(storagePartners);
				applyPartnerResolution(storagePartners);
			} catch (err) {
				console.error('Error loading partners:', err);
			} finally {
				setIsLoadingPartners(false);
			}
		};

		loadPartners();
	}, [allPartners, applyPartnerResolution]);

	const validateEIN = (ein: string): boolean => {
		const einRegex = /^\d{2}-\d{7}$/;
		return einRegex.test(ein);
	};

	const validateSettings = (): boolean => {
		if (!settingsBuffer.ein.trim()) {
			setError('EIN is required');
			return false;
		}

		if (!validateEIN(settingsBuffer.ein)) {
			setError('Invalid EIN format. Expected format: XX-XXXXXXX (e.g., 12-3456789)');
			return false;
		}

		if (!settingsBuffer.partnerId && partners.length === 0) {
			setError('No storage provider partner configured. Please configure partners first.');
			return false;
		}

		if (!settingsBuffer.workplace) {
			setError('Workplace is required');
			return false;
		}

		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!validateSettings()) {
			return;
		}

		try {
			setIsSubmitting(true);

			if (!settingsBuffer.partnerId) {
				await createOnboardingSettings({
					ein: settingsBuffer.ein,
					storageProvider: '',
					provider: null,
					workplace: settingsBuffer.workplace,
				});
			} else {
				const selectedPartner = findPartnerByIri(partners, settingsBuffer.partnerId);
				await createOnboardingSettings({
					ein: settingsBuffer.ein,
					storageProvider: selectedPartner?.label || settingsBuffer.partnerId,
					provider: settingsBuffer.partnerId,
					workplace: settingsBuffer.workplace,
				});
			}

			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
		}
	};

	const handlePartnerChange = (event: SelectChangeEvent<string>) => {
		const partnerIri = event.target.value;

		if (partnerIri === '' && settingsBuffer.partnerId) {
			setClearConfirmOpen(true);
			return;
		}

		setSettingsBuffer((prev) => ({
			...prev,
			partnerId: partnerIri,
		}));
	};

	const handleClearConfirm = () => {
		setSettingsBuffer((prev) => ({
			...prev,
			partnerId: '',
		}));
		setClearConfirmOpen(false);
	};

	const handleClearCancel = () => {
		setClearConfirmOpen(false);
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit General Settings</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Employer Identification Number (EIN)"
					name="ein"
					value={settingsBuffer.ein}
					onChange={(e) => setSettingsBuffer({ ...settingsBuffer, ein: e.target.value })}
					placeholder="XX-XXXXXXX"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					helperText="Format: XX-XXXXXXX (e.g., 12-3456789)"
					inputProps={{
						maxLength: 10,
					}}
				/>

				<FormControl fullWidth margin="normal" className={styles.input}>
					<InputLabel id="storage-provider-select-label" shrink>
						Storage Provider
					</InputLabel>
					<Select
						labelId="storage-provider-select-label"
						value={settingsBuffer.partnerId}
						onChange={handlePartnerChange}
						label="Storage Provider"
						disabled={isLoadingPartners || isSubmitting}
						displayEmpty
						renderValue={(selected) => {
							if (!selected) {
								return 'None';
							}
							return findPartnerByIri(partners, selected)?.label ?? selected;
						}}
					>
						<MenuItem value="">None</MenuItem>
						{partners.map((partner) => (
							<MenuItem key={partner.id} value={getPartnerIri(partner)}>
								{partner.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{!settingsBuffer.partnerId && !isLoadingPartners && (
					<Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
						Removing the storage provider will prevent onboarding documents from being saved to
						your connected storage. Employees may be unable to complete document uploads until a
						provider is configured again.
					</Alert>
				)}

				{!isLoadingPartners && partners.length === 0 && (
					<div style={{ color: '#ed6c02', marginTop: 8, fontSize: '0.875rem' }}>
						No storage provider partner configured. Please configure in Partners section first.
					</div>
				)}
			</form>

			<Dialog open={clearConfirmOpen} onClose={handleClearCancel}>
				<DialogTitle>Remove Storage Provider</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to remove the storage provider? Without a storage provider,
						onboarding documents cannot be saved to your connected storage and employees may
						encounter issues completing document uploads.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<UiButton className={styles.mabelButton} onClick={handleClearCancel}>
						Cancel
					</UiButton>
					<UiButton className={styles.mabelButton} onClick={handleClearConfirm}>
						Remove
					</UiButton>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default EditGeneralSettings;
